/* oxlint-disable no-use-before-define */
import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import Chip from '@material-ui/core/Chip';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import clsx from 'clsx';
import tw from 'twin.macro';
import _ from 'lodash';
import { CircularProgress, debounce, makeStyles } from '@material-ui/core';

const TwAutocomplete = ({
  options,
  values,
  onChange,
  placeholderText,
  classes,
  isMultiple,
  onInputChange,
  isNested,
  disableClearable,
  disableCloseOnSelect,
  forcePopupIcon,
  textFieldVariant,
  freeSolo,
  autoFocus,
  onBlur,
  onFocus,
  disabled,
  isLoading,
  hidePopperWhenEmpty,
  openOnFocus,
  autoComplete,
  renderGroup,
  filterOptions,
  getOptionSelected,
}) => {
  const [flattenedOptions, setFlattenedOptions] = useState([]);
  const [isPopperOpen, setPopperOpen] = useState(false);

  const handleInputChange = (event) => {
    const newValue = event && event.target && event.target.value;
    if (onInputChange) onInputChange(newValue);
  };

  const debounceHandleInputChange = debounce(handleInputChange, 300);

  const handleChange = (event, newValue) => {
    onChange(newValue);
  };

  const useStyles = makeStyles(() => ({
    listbox: {
      '& .MuiListSubheader-root': tw`font-body`,
      '& .MuiAutocomplete-option': tw`font-body`,
    },
  }));

  const groupLableAndOptions = useStyles();

  useEffect(() => {
    if (options && _.isArray(options)) {
      let fo = options.map((v) => [
        ...(v.suggests || []).map((v1) => ({
          ...v1,
          group: v.label,
          locationType: v.value,
        })),
      ]);
      if (fo.length) fo = fo.reduce((a, b) => [...a, ...b]);
      setFlattenedOptions(fo);
    }
  }, [options]);

  // Check if popup should be hidden when empty

  const shouldHidePopper =
    hidePopperWhenEmpty && (!flattenedOptions || flattenedOptions.length === 0);

  const handlePopperOpen = () => {
    if (!shouldHidePopper) {
      setPopperOpen(true);
    }
  };

  // Options can share a value and differ only by type, like an airport and its metropolitan area
  const defaultGetOptionSelected = (option, value) =>
    option.value === value.value &&
    (option.group || '') === (value.group || '') &&
    (option.type || '') === (value.type || '');

  return (
    <Autocomplete
      multiple={isMultiple}
      id="tags-filled"
      disablePortal={isNested}
      groupBy={(option) => option.group || ''}
      // options={(flattenedOptions || [])
      //  .sort((a, b) => -(a.group || '').localeCompare(b.group || ''))}
      options={flattenedOptions || []}
      getOptionLabel={(option) => option.label || ''}
      getOptionSelected={getOptionSelected || defaultGetOptionSelected}
      classes={{ ...classes, ...groupLableAndOptions }}
      limitTags={2}
      getOptionDisabled={(option) =>
        (option.label || '').includes('(restricted)') || (option.label || '').includes('(disabled)')
      }
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          // oxlint-disable-next-line react/jsx-props-no-spreading
          <Chip
            key={`${index.toString()}`}
            variant="default"
            label={option.label}
            {...getTagProps({ index })}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...params}
          variant={textFieldVariant || undefined}
          label=""
          className={clsx('w-full', {
            'flex items-center relative border border-gray-500 border-opacity-25 px-2 placeholder-gray-700':
              textFieldVariant === TwAutocomplete.defaultProps.textFieldVariant,
            'h-full': isMultiple,
          })}
          placeholder={placeholderText}
          onBlur={onBlur}
          onFocus={onFocus}
          onChange={debounceHandleInputChange}
          autoFocus={autoFocus}
          // Prevent open browser autocomplete
          inputProps={{
            ...params.inputProps,
            autoComplete:
              autoComplete ||
              (placeholderText &&
              (placeholderText.toLowerCase().indexOf('country') > -1 ||
                placeholderText.toLowerCase().indexOf('travelers') > -1)
                ? 'new-password'
                : 'off'),
            'aria-autocomplete': 'none',
          }}
          // oxlint-disable-next-line react/jsx-no-duplicate-props
          InputProps={{
            ...params.InputProps,
            endAdornment: !isLoading ? undefined : (
              <div className="absolute right-0 mr-2">
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </div>
            ),
          }}
        />
      )}
      disabled={disabled}
      onChange={handleChange}
      value={values || null}
      forcePopupIcon={forcePopupIcon}
      freeSolo={freeSolo}
      disableClearable={disableClearable}
      disableCloseOnSelect={disableCloseOnSelect}
      onOpen={handlePopperOpen}
      openOnFocus={openOnFocus}
      onClose={() => setPopperOpen(false)}
      open={shouldHidePopper ? false : isPopperOpen}
      /// If onInputChange defined, assumed autocomplete options, related to query string,
      /// is returned from backend
      /// In case cruise-lines / ships filter, we assigning all autocomplete options on
      /// page load. So, assign filterOptions with undefined to use default method to filter list
      filterOptions={filterOptions || (onInputChange ? (_options) => _options : undefined)}
      renderGroup={renderGroup}
    />
  );
};

TwAutocomplete.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      suggests: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        }),
      ),
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  values: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }).isRequired,
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
    ).isRequired,
  ]),
  onChange: PropTypes.func.isRequired,
  placeholderText: PropTypes.string,
  // oxlint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object,
  onInputChange: PropTypes.func,
  isMultiple: PropTypes.bool,
  isNested: PropTypes.bool,
  disableClearable: PropTypes.bool,
  disableCloseOnSelect: PropTypes.bool,
  forcePopupIcon: PropTypes.bool,
  textFieldVariant: PropTypes.string,
  freeSolo: PropTypes.bool,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  isLoading: PropTypes.bool,
  hidePopperWhenEmpty: PropTypes.bool,
  openOnFocus: PropTypes.bool,
  autoComplete: PropTypes.string,
  renderGroup: PropTypes.func,
  filterOptions: PropTypes.func,
  getOptionSelected: PropTypes.func,
};

TwAutocomplete.defaultProps = {
  options: undefined,
  values: undefined,
  placeholderText: '',
  classes: undefined,
  onInputChange: undefined,
  isMultiple: true,
  isNested: false,
  disableClearable: false,
  disableCloseOnSelect: false,
  forcePopupIcon: false,
  textFieldVariant: 'filled',
  freeSolo: true,
  autoFocus: true,
  disabled: false,
  onBlur: () => {},
  onFocus: () => {},
  isLoading: false,
  hidePopperWhenEmpty: false,
  openOnFocus: false,
  autoComplete: undefined,
  renderGroup: undefined,
  filterOptions: undefined,
  getOptionSelected: undefined,
};

export default memo(TwAutocomplete);
